import { create } from 'zustand';
import { devtools, persist, StateStorage, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { GeniiusUser } from '@/lib/types';
import isEqual from 'lodash/isEqual';
import { logger } from '@/lib/logger'
import _ from 'lodash'
import { getClerkPrivateMetadata, updateClerkPrivateMetadata } from '@/app/actions/auth';
import { fetchFollowing, fetchFollowers } from '@/app/actions/followers';

import { updateUser,
  getInitialUser,
  addNewUser,
  getReferredUsers,
  fetchProfileViewsCount,
  getUserAiUsage,
  getVerificationStatus,
  addNewSession } from '@/app/actions/users';
import { getSecondaryUserData } from '@/app/actions/general';
import { customStorage } from '@/lib/hooks/zustand-stores/utils'
import { getOrganizationUsers } from '@/app/actions/organizations'

// Create a logger with store context
const getStoreLogger = () => {
  const user = useUserStore.getState().user;
  const userId = user?._id || null;
  const userFullName = user?.fullName || null;

  const additionalContext = {
    userId: userId,
    userFullName: userFullName,
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'server',
    os: typeof window !== 'undefined' ? window.navigator.userAgent : 'server',
    level: 'info',
    timestamp: new Date().toISOString()
  };

  const isProduction = process.env.NODE_ENV === 'production';
  return isProduction
    ? logger.child({ ...additionalContext, level: 'silent' })
    : logger.child(additionalContext);
};

const LIMIT_USERS = 10
const CACHE_EXPIRY = 24 * 60 * 60 * 1000 * 30; // 30 days

// Helper function to extract cacheable fields from user data
const extractCacheableFields = (userData: GeniiusUser | null) => {
  if (!userData) return null;
  return {
    _id: userData._id,
    clerkUserId: userData.clerkUserId,
    firstName: userData.firstName,
    fullName: userData.fullName,
    lastName: userData.lastName,
    username: userData.username,
    createdAt: userData.createdAt,
    imageUrl: userData.imageUrl,
    bio: userData.bio,
    subscriptionDetails: userData.subscriptionDetails,
    ThemeProfileImageVersion: userData.ThemeProfileImageVersion,
    hasThemeProfileImage: userData.hasThemeProfileImage,
    settings: userData.settings,
    verified: userData.verified,
    verificationRequestStatus: userData.verificationRequestStatus,
    aiUsage: userData.aiUsage,
    organizations: userData.organizations,
    referredBy: userData.referredBy,
    organizationMembers: userData.organizationMembers
  };
};

const saveSessionLocationInUser = async (userId: string, country?: string, city?: string) => {
  try {
    const updatePromises = [];
    if (city) {
      updatePromises.push(updateUser(userId, 'currentSessionLocation.city', city));
    }
    if (country) {
      updatePromises.push(updateUser(userId, 'currentSessionLocation.country', country));
    }
    await Promise.all(updatePromises);
  } catch (e) {
    getStoreLogger().error('Failed to save session location:', e);
  }
}

// Define the state interface
type UserState = {
  user: GeniiusUser | null;
  loading: boolean;
  loadingFollowing: boolean;
  loadingFollowers: boolean;
  loadingProfileViews: boolean;
  loadingReferrals: boolean;
  error: Error | null;
  lastCached: number | null;
};

// Define the actions interface
type UserActions = {
  setUser: (user: GeniiusUser | null) => void;
  setLoading: (loading: boolean) => void;
  setLoadingFollowing: (loading: boolean) => void;
  setLoadingFollowers: (loading: boolean) => void;
  setLoadingProfileViews: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  refreshUser: (userId: string, clerkUser: any) => Promise<void>;
  refreshFollowData: (userId: string) => Promise<void>;
  updateUserData: (updates: Partial<GeniiusUser>) => void;
  profileViewsCount: (userId: string) => Promise<void>;
  refreshAiUsageAndSubscriptionData: (userId: string) => Promise<void>;
  VerificationStatus: (userId: string) => Promise<void>;
  saveSessionToDb: (clerkUser: any, userId: string) => Promise<void>;
  updateClerkUnsafeUserMetadata: (clerkUser: any, userData: GeniiusUser) => Promise<void>;
  saveSubscriptionDataToClerk: (clerkUserId: string, userData: GeniiusUser) => Promise<void>;
  cleanUserState: () => void;
  updateUserBio: (userId: string, newBio: any) => void;
  updateUserSettings: (userId: string, newSettings: any) => void;
  updateUserThemePhotoData: (userId: string, ThemeProfileImageVersion: number, hasThemeProfileImage: boolean ) => void;
  updateUserCountry: (userId: string, newCountry: string) => void;
  updateUserState: (userId: string, newState: string) => void;
  updateUserCity: (userId: string, newCity: string) => void;
  updateUserVerificationRequestStatus: (userId: string, newStatus: string) => void;
  updateUser: () => Promise<void>;
  refreshOrganizationMembers: (userId: string) => Promise<void>;
  setLoadingReferrals: (loading: boolean) => void;
  refreshReferralData: (username: string) => Promise<void>;
  initializeSecondaryData: () => Promise<void>;
  refreshOrganizations: (clerkUser: any) => Promise<void>;
};

// Helper functions
const addFollowData = async (userId: string) => {
  try {
    const [following, followers] = await Promise.all([
      fetchFollowing(userId),
      fetchFollowers(userId),
    ]);
    getStoreLogger().info(`[Follow Data Fetched] For ID: ${userId}`);
    return { following, followers };
  } catch (error) {
    getStoreLogger().error('Error fetching follow data', error);
    return { following: [], followers: [] };
  }
};

const getLastSession = async (clerkUser: any) => {
  const userSessions = await clerkUser?.getSessions();
  return userSessions.sort(
    (a: any, b: any) =>
      new Date(b.lastActiveAt).getTime() - new Date(a.lastActiveAt).getTime()
  )[0];
};

const getSessionLocation = (lastSession: any) => {
  const city = lastSession?.latestActivity?.city;
  const country = lastSession?.latestActivity?.country;
  return { city, country };
};

// Function to create or retrieve a user from the database
const getSavedUser = async (userId: string, clerkUser: any): Promise<GeniiusUser | null> => {
  try {
    let userData = await getInitialUser(userId);

    // If user data doesn't exist, create the user and fetch it again
    if (!userData) {
      getStoreLogger().info(`User ${userId} not found. Creating new user.`);
      const newUser = await addNewUser(JSON.parse(JSON.stringify(clerkUser)));
      userData = await getInitialUser(newUser?._id);
    }

    return userData;
  } catch (error) {
    getStoreLogger().error('Error fetching or creating user:', error);
    return null;
  }
};

// Create the Zustand store
const useUserStore = create<UserState & UserActions>()(
  devtools(
    persist(
      immer((set, get) => ({
        // Initial state
        user: null as GeniiusUser | null,
        loading: true,
        loadingFollowing: false,
        loadingFollowers: false,
        loadingProfileViews: false,
        loadingReferrals: false,
        error: null,
        lastCached: null,

        // Actions
        setUser: (user) =>
          set((state) => {
            if (user && user._id && !isEqual(state.user, user)) {
              state.user = user;
              state.lastCached = Date.now();
            }
          }),

        setLoading: (loading) => set({ loading }),

        setLoadingFollowing: (loading) => set({ loadingFollowing: loading }),

        setLoadingFollowers: (loading) => set({ loadingFollowers: loading }),

        setLoadingProfileViews: (loading) => set({ loadingProfileViews: loading }),

        setError: (error) => set({ error }),

        setLoadingReferrals: (loading) => set({ loadingReferrals: loading }),

        refreshReferralData: async (username: string) => {
          try {
            get().setLoadingReferrals(true);
            const referredUsers = await getReferredUsers(username, LIMIT_USERS);

            set((state) => {
              if (state.user) {
                state.user.referredUsers = referredUsers;
              }
            });
          } catch (error) {
            getStoreLogger().error('Error refreshing referral data:', error);
          } finally {
            get().setLoadingReferrals(false);
          }
        },

        updateUser: async () => {
          const userId = get().user?._id
          try {
            if (userId) {
            const userData = await getInitialUser(userId);
            if (userData) {
              get().setUser(userData);
            }
            }
          } catch (e) {
            getStoreLogger().error(`failed to update user ${userId}`)
          }
        },

        refreshUser: async (userId, clerkUser) => {
          set({ loading: true });
          let userData: GeniiusUser | null = null;

          const organizations = clerkUser?.organizationMemberships?.map((membership: any) => ({
            id: membership.organization.id,
            name: membership.organization.name,
            role: membership.role,
            slug: membership.organization.slug,
            created_at: new Date(membership.organization.createdAt).getTime(),
            image_url: membership.organization.imageUrl,
            logo_url: membership.organization.imageUrl
          })) || [];

          const cachedUser = get().user;
          const lastCached = get().lastCached;
          if (cachedUser && lastCached && (Date.now() - lastCached < CACHE_EXPIRY)) {
            getStoreLogger().info(`[Cache Hit] User data fetched from cache for ID: ${userId}`);
            userData = cachedUser;
          } else {
            getStoreLogger().info(`[Cache Miss] Fetching user data or creating a new user for ID: ${userId}`);
            userData = await getSavedUser(userId, clerkUser);
          }

          if (userData) {
            if (!userData.organizations || userData.organizations.length === 0) {
              await updateUser(userId, 'organizations', organizations)
            }
            // Create a new object with the updated organizations
            const updatedUserData = {
              ...userData,
              organizations: organizations
            };
            get().setUser(updatedUserData);
          } else {
            get().setError(new Error('Failed to get or create user data'));
          }

          set({ loading: false });
        },

        initializeSecondaryData: async () => {
          // Skip if SKIP_SECONDARY_DATA is true
          if (process.env.NEXT_PUBLIC_SKIP_SECONDARY_DATA === 'true') {
            getStoreLogger().info('Skipping secondary data initialization due to SKIP_SECONDARY_DATA env var');
            return;
          }

          const userId = get().user?._id;
          const username = get().user?.username;

          if (userId && username) {
            try {
              const secondaryData = await getSecondaryUserData(userId, username, {
                fetchFollowData: true,
                fetchProfileViews: false,
                fetchVerificationStatus: true,
                fetchAiUsage: true,
                fetchOrganizationMembers: true,
                fetchReferralData: true
              });

              if (secondaryData) {
                set((state) => {
                  if (state.user) {
                    state.user.following = secondaryData.following;
                    state.user.followers = secondaryData.followers;
                    state.user.profileViewsCount = secondaryData.profileViewsCount;
                    state.user.verified = secondaryData.verified;
                    state.user.verificationRequestStatus = secondaryData.verificationRequestStatus;
                    state.user.aiUsage = secondaryData.aiUsage;
                    state.user.subscriptionDetails = secondaryData.subscriptionDetails;
                    state.user.organizationMembers = secondaryData.organizationMembers;
                    state.user.referredUsers = secondaryData.referredUsers;
                    state.user.settings = secondaryData.settings;
                  }
                });
              }
            } catch (error) {
              getStoreLogger().error('Error fetching secondary user data:', error);
              get().setError(error as Error);
            }
          }
        },

        updateUserBio: (userId: string, newBio: any) => set((state) => {
          if (state.user && state.user._id === userId) {
            state.user.bio = newBio;
          }
        }),

        updateUserCountry: (userId: string, newCountry: string) => set((state) => {
          if (state.user && state.user._id === userId) {
            state.user.bio = state.user.bio || {};
            state.user.bio.location = state.user.bio.location || {};
            state.user.bio.location.country = newCountry;
          }
        }),

        updateUserState: (userId: string, newState: string) => set((state) => {
          if (state.user && state.user._id === userId) {
            state.user.bio = state.user.bio || {};
            state.user.bio.location = state.user.bio.location || {};
            state.user.bio.location.state = newState;
          }
        }),

        updateUserCity: (userId: string, newCity: string) => set((state) => {
          if (state.user && state.user._id === userId) {
            state.user.bio = state.user.bio || {};
            state.user.bio.location = state.user.bio.location || {};
            state.user.bio.location.city = newCity;
          }
        }),

        updateUserVerificationRequestStatus: (userId: string, newStatus: string) => set((state) => {
          if (state.user && state.user._id === userId) {
            state.user.verificationRequestStatus = newStatus;
          }
        }),

        updateUserSettings: (userId: string, newSettings: any) => set((state) => {
          if (state.user && state.user._id === userId) {
            state.user.settings = newSettings;
          }
        }),

        updateUserThemePhotoData: (userId: string,
                                   ThemeProfileImageVersion: number,
                                   hasThemeProfileImage: boolean) => set((state) => {
          if (state.user && state.user._id === userId) {
            state.user.ThemeProfileImageVersion = ThemeProfileImageVersion;
            state.user.hasThemeProfileImage = hasThemeProfileImage;
          }
        }),

        refreshFollowData: async (userId) => {
          set({ loadingFollowing: true, loadingFollowers: true });
          try {
            const { following, followers } = await addFollowData(userId);
            const currentUser = get().user;
            if (currentUser) {
              const updatedUser: GeniiusUser = {
                ...currentUser,
                following,
                followers
              };
              get().setUser(updatedUser);
            }
          } catch (error) {
            get().setError(error as Error);
          } finally {
            set({ loadingFollowing: false, loadingFollowers: false });
          }
        },

        updateUserData: (updates: Partial<GeniiusUser>) => set((state) => {
          if (state.user) {
            const updatedUser = {
              ...state.user,
              ...updates
            };
            state.user = updatedUser;
          }
        }),

        profileViewsCount: async (userId) => {
          set({ loadingProfileViews: true });
          try {
            const profileViewsCountData = await fetchProfileViewsCount(userId);
            set((state) => {
              if (state.user) {
                state.user.profileViewsCount = profileViewsCountData.count;
              }
            });
          } catch (error) {
            get().setError(error as Error);
          } finally {
            set({ loadingProfileViews: false });
          }
        },

        refreshAiUsageAndSubscriptionData: async (userId: string) => {
          try {
            const userAiUsage = await getUserAiUsage(userId);
            set((state) => {
              if (state.user) {
                state.user.aiUsage = userAiUsage.aiUsage;
                state.user.subscriptionDetails = userAiUsage.subscriptionDetails;
              }
            });
          } catch (error) {
            get().setError(error as Error);
        }},

        VerificationStatus: async (userId: string) => {
          try {
            const verificationStatus = await getVerificationStatus(userId);
            set((state) => {
              if (state.user) {
                state.user.verified = verificationStatus.verified;
                state.user.verificationRequestStatus = verificationStatus.verificationRequestStatus;
              }
            });
          } catch (error) {
            get().setError(error as Error);
          }
        },

        // Save session to the database
        saveSessionToDb: async (clerkUser, userId) => {
          const lastSession = await getLastSession(clerkUser);
          const plainLastSession = { ...lastSession, userId };
          const location = getSessionLocation(lastSession);
          try {
            await addNewSession(plainLastSession);
            await saveSessionLocationInUser(userId, location.country, location.city);
          } catch (e) {
            getStoreLogger().error('Failed to save session location:', e);
          }
        },

        // Update Clerk's unsafe metadata
        updateClerkUnsafeUserMetadata: async (clerkUser, userData) => {
          if (clerkUser && userData && userData._id && !clerkUser.unsafeMetadata?._id) {
            await clerkUser.update({
              unsafeMetadata: {
                _id: userData._id,
              },
            });
          }
        },

        // Save subscription data to Clerk
        saveSubscriptionDataToClerk: async (clerkUserId, userData) => {
          if (userData.subscriptionDetails) {
            try {
              const currentClerkPrivateMD = await getClerkPrivateMetadata(clerkUserId)
              const fieldsToCompare = ['team_id', 'is_admin', 'is_active', 'product_id'];
              const areSubscriptionDetailsEqual = fieldsToCompare.every(field =>
                currentClerkPrivateMD.data.subscriptionDetails[field] === userData.subscriptionDetails[field]
              );

              if (!areSubscriptionDetailsEqual)
              {
                await updateClerkPrivateMetadata(clerkUserId, 'subscriptionDetails', userData.subscriptionDetails);
                getStoreLogger().info(`updated subscriptionDetails in clerk private meta data for user ${clerkUserId}`, userData.subscriptionDetails)
              }
              else {
                getStoreLogger().info('user already have updated subscriptionDetails in clerk private metadata so no need for update')
              }
            } catch (e) {
              getStoreLogger().error('Failed to save subscription details:', e);
            }
          }
        },

        cleanUserState: () => {
          set({
            user: null,
            loading: false,
            loadingFollowing: false,
            loadingFollowers: false,
            loadingProfileViews: false,
            loadingReferrals: false,
            error: null
          });
        },

        refreshOrganizationMembers: async (userId) => {
          try {
            const currentUser = get().user;
            if (currentUser?.organizations) {
              const orgMembers = new Map();

              for (const org of currentUser.organizations) {
                const members = await getOrganizationUsers(org.slug, LIMIT_USERS);
                members.members.forEach((member: any) => {
                  // If member already exists, increment their shared communities count
                  if (orgMembers.has(member._id)) {
                    const existingMember = orgMembers.get(member._id);
                    existingMember.sharedCommunitiesCount = (existingMember.sharedCommunitiesCount || 1) + 1;
                    existingMember.organizationName = `${existingMember.organizationName}, ${org.name}`;
                    existingMember.organizationSlug = `${existingMember.organizationSlug}, ${org.slug}`;
                    orgMembers.set(member._id, existingMember);
                  } else {
                    // Add new member with first org
                    orgMembers.set(member._id, {
                      ...member,
                      sharedCommunitiesCount: 1,
                      organizationName: org.name,
                      organizationSlug: org.slug
                    });
                  }
                });
              }

              set((state) => {
                if (state.user) {
                  state.user.organizationMembers = Array.from(orgMembers.values());
                }
              });
            }
          } catch (error) {
            getStoreLogger().error('Error fetching organization members:', error);
            get().setError(error as Error);
          }
        },

        refreshOrganizations: async (clerkUser) => {
          try {
            const userId = get().user?._id;
            if (!userId || !clerkUser) {
              getStoreLogger().error('Cannot refresh organizations: missing userId or clerkUser');
              return;
            }

            const organizations = clerkUser?.organizationMemberships?.map((membership: any) => ({
              id: membership.organization.id,
              name: membership.organization.name,
              role: membership.role,
              slug: membership.organization.slug,
              created_at: new Date(membership.organization.createdAt).getTime(),
              image_url: membership.organization.imageUrl,
              logo_url: membership.organization.imageUrl
            })) || [];

            // Update the user in the store
            set((state) => {
              if (state.user) {
                state.user.organizations = organizations;
              }
            });

            getStoreLogger().info(`[Organizations Refreshed] For user ID: ${userId}`);
          } catch (error) {
            getStoreLogger().error('Error refreshing organizations:', error);
            get().setError(error as Error);
          }
        },
      })),
      {
        name: 'user-storage',
        storage: createJSONStorage(() => customStorage as any),
        version: 1,
        partialize: (state) => ({
          user: extractCacheableFields(state.user),
          lastCached: state.lastCached,
        }),
      }
    )
  )
);

export default useUserStore;
